import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Loading from './Loading';
import './InfluencerModalTikTokDetalle.css';

const InfluencerModalTikTokDetalle = ({ influencer, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    loadTikTokInsights();
  }, [influencer.id]);

  const loadTikTokInsights = async () => {
    setLoading(true);
    setError(null);

    try {
      // Construir la URL del backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/content';
      const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');

      const response = await axios.get(
        `${backendUrl}/tiktok/influencer/${influencer.id}/insights/`,
        {
          withCredentials: true
        }
      );

      console.log('📊 TikTok Insights recibidos:', response.data);

      // Verificar si la respuesta es HTML en lugar de JSON
      if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
        throw new Error('Endpoint no disponible');
      }

      setInsights(response.data);
    } catch (err) {
      console.error('❌ Error cargando insights de TikTok:', err);

      // Verificar si es un error de token expirado (401)
      if (err.response && err.response.status === 401) {
        setError(err.response.data.error || 'El token de TikTok ha expirado. Por favor, reconecta la cuenta.');
      } else if (typeof err.response?.data === 'string' && err.response.data.includes('<!doctype html>')) {
        // Si el endpoint no existe, mostrar la información básica del influencer
        console.log('ℹ️ Mostrando información básica del influencer');
        setInsights({
          basic_info: true,
          account_stats: {
            display_name: influencer.name,
            username: influencer.username,
            tiktok_connected: influencer.tiktok_connected
          }
        });
      } else {
        // Otros errores
        setError(err.response?.data?.error || 'Error al cargar los datos de TikTok');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReconnectTikTok = () => {
    if (!influencer?.id) return;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');
    const connectUrl = `${backendUrl}/api/tiktok/connect-influencer/${influencer.id}/`;

    console.log('🔗 Reconectando TikTok:', connectUrl);
    window.open(connectUrl, 'TikTokAuth', 'width=600,height=700');
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container tiktok-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-content">
            <img
              src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/logos/logo-tiktok-3.avif"
              alt="TikTok"
              className="platform-logo"
            />
            <div>
              <h2 className="modal-title">{influencer.name}</h2>
              <p className="modal-subtitle">Estadísticas de TikTok</p>
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {loading && (
            <Loading fullScreen={false} />
          )}

          {error && (
            <div className="error-state">
              <p className="error-message">{error}</p>
              {error.includes('expirado') || error.includes('expired') ? (
                <button className="btn-retry" onClick={handleReconnectTikTok}>
                  🔗 Reconectar TikTok
                </button>
              ) : (
                <button className="btn-retry" onClick={loadTikTokInsights}>
                  🔄 Reintentar
                </button>
              )}
            </div>
          )}

          {!loading && !error && insights && (
            <div className="insights-content">
              {/* Info básica cuando el endpoint no está disponible */}
              {insights.basic_info && (
                <div className="info-notice">
                  <div className="notice-icon">ℹ️</div>
                  <div className="notice-content">
                    <h3 className="notice-title">Cuenta de TikTok Conectada</h3>
                    <p className="notice-description">
                      Este influencer tiene una cuenta de TikTok conectada.
                      Las estadísticas detalladas estarán disponibles próximamente.
                    </p>
                    <div className="basic-info-card">
                      <div className="info-row">
                        <span className="info-icon">👤</span>
                        <div>
                          <div className="info-label">Nombre</div>
                          <div className="info-value">{influencer.name}</div>
                        </div>
                      </div>
                      {influencer.username && (
                        <div className="info-row">
                          <span className="info-icon">@</span>
                          <div>
                            <div className="info-label">Usuario</div>
                            <div className="info-value">@{influencer.username}</div>
                          </div>
                        </div>
                      )}
                      <div className="info-row">
                        <span className="info-icon">✅</span>
                        <div>
                          <div className="info-label">Estado</div>
                          <div className="info-value">TikTok Conectado</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Información del Perfil */}
              {!insights.basic_info && (insights.avatar_url || insights.username || insights.display_name) && (
                <div className="profile-section">
                  <h3 className="section-title">👤 Información del Perfil</h3>
                  <div className="profile-content">
                    {insights.avatar_url && (
                      <img
                        src={insights.avatar_url}
                        alt={insights.username || 'Avatar'}
                        className="profile-avatar"
                      />
                    )}
                    <div className="profile-details">
                      {insights.username && (
                        <h4 className="profile-username">@{insights.username}</h4>
                      )}
                      {insights.display_name && (
                        <p className="profile-display-name">
                          <strong>Nombre:</strong> {insights.display_name}
                        </p>
                      )}
                      {insights.tiktok_user_id && (
                        <p className="profile-user-id">
                          <strong>User ID:</strong> {insights.tiktok_user_id}
                        </p>
                      )}
                      {insights.connected_at && (
                        <p className="profile-connected">
                          <strong>Conectada:</strong> {new Date(insights.connected_at).toLocaleDateString('es-ES')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Estadísticas de la Cuenta */}
              {!insights.basic_info && (
                insights.followers_count !== undefined ||
                insights.following_count !== undefined ||
                insights.likes_count !== undefined ||
                insights.video_count !== undefined
              ) && (
                <div className="stats-section">
                  <h3 className="section-title">📊 Estadísticas de la Cuenta</h3>
                  <div className="stats-grid-tiktok">
                    {insights.followers_count !== undefined && (
                      <div className="stat-box-tiktok">
                        <div className="stat-number">{formatNumber(insights.followers_count)}</div>
                        <div className="stat-label-box">Seguidores</div>
                      </div>
                    )}
                    {insights.following_count !== undefined && (
                      <div className="stat-box-tiktok">
                        <div className="stat-number">{formatNumber(insights.following_count)}</div>
                        <div className="stat-label-box">Siguiendo</div>
                      </div>
                    )}
                    {insights.likes_count !== undefined && (
                      <div className="stat-box-tiktok">
                        <div className="stat-number">{formatNumber(insights.likes_count)}</div>
                        <div className="stat-label-box">Likes</div>
                      </div>
                    )}
                    {insights.video_count !== undefined && (
                      <div className="stat-box-tiktok">
                        <div className="stat-number">{formatNumber(insights.video_count)}</div>
                        <div className="stat-label-box">Videos</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Videos Publicados */}
              {!insights.basic_info && insights.videos && insights.videos.length > 0 && (
                <div className="videos-section">
                  <h3 className="section-title">🎬 Videos Publicados</h3>
                  <div className="videos-grid-tiktok">
                    {insights.videos.map((video, idx) => (
                      <div key={idx} className="video-card-tiktok">
                        {video.cover_image_url ? (
                          <img
                            src={video.cover_image_url}
                            alt="Video thumbnail"
                            className="video-thumbnail"
                          />
                        ) : (
                          <div className="video-thumbnail-placeholder"></div>
                        )}
                        <div className="video-info">
                          <div className="video-title">
                            {video.title || video.video_description || 'Sin título'}
                          </div>
                          <div className="video-stats-row">
                            {video.view_count !== undefined && (
                              <span>👁️ {formatNumber(video.view_count)}</span>
                            )}
                            {video.like_count !== undefined && (
                              <span>❤️ {formatNumber(video.like_count)}</span>
                            )}
                            {video.comment_count !== undefined && (
                              <span>💬 {formatNumber(video.comment_count)}</span>
                            )}
                          </div>
                          {video.create_time && (
                            <p className="video-date">
                              {new Date(video.create_time * 1000).toLocaleDateString('es-ES')}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state si no hay datos */}
              {!insights.basic_info &&
               !insights.avatar_url &&
               !insights.username &&
               !insights.display_name &&
               insights.followers_count === undefined &&
               insights.following_count === undefined &&
               insights.likes_count === undefined &&
               insights.video_count === undefined &&
               (!insights.videos || insights.videos.length === 0) && (
                <div className="empty-state">
                  <p>No hay datos disponibles para este influencer de TikTok</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfluencerModalTikTokDetalle;
